import { Box, Flex, Tooltip, useBreakpointValue } from '@chakra-ui/react';
import { ButtonIcon, TagInput } from '@morressier/alembic';
import { Controller, get, useFormContext } from 'react-hook-form';
import { FieldLabel } from '../Field';
import { useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
type ValidationErrorFns = {
  checkMaxCountOfKeywords: (max: number, label: string) => string;
  checkMaxLength: (max: number) => string;
  checkMinCountOfKeywords: (min: number, label: string) => string;
  checkMinLength: (min: number) => string;
};
export const errorMessageFns: ValidationErrorFns = {
  checkMaxCountOfKeywords: (max, label) => `Maximum ${max} ${label} allowed.`,
  checkMaxLength: max => `Must be ${max} characters or fewer.`,
  checkMinCountOfKeywords: (min, label) => `Minimum ${min} ${label} required.`,
  checkMinLength: min => `Must be at least ${min} characters.`
};
const InputArrayField = ({
  tempFieldName,
  fieldName,
  isShowInfo,
  isHidSubtitle,
  validations,
  labels,
  placeholder,
  errorLabelName
}: {
  tempFieldName: string;
  fieldName: string;
  isShowInfo?: boolean;
  isHidSubtitle?: boolean;
  validations?: {
    required?: boolean;
    maxCountOfKeywords?: number;
    maxLength?: number;
    minCountOfKeywords?: number;
    minLength?: number;
  };
  labels: { main: string; subtitle?: string; helpText?: string };
  placeholder?: string;
  errorLabelName?: string;
}) => {
  const form = useFormContext();

  const {
    setValue,
    formState: { errors },
    control,
    trigger
  } = form;

  const tempValues = form.watch(tempFieldName);
  const tagInputWrapperRef = useRef<HTMLDivElement>(null);
  const errorLabel = errorLabelName || 'keywords';

  const updatedPlaceHolder = placeholder?.includes(
    'Enter keyword and press Enter'
  )
    ? 'keyword'
    : placeholder;

  const inputPlaceholder = useBreakpointValue({
    base: 'Type and tap + to add',
    md: `Type and press Enter to add ${updatedPlaceHolder}`
  });

  const helperText = useBreakpointValue<
    (validations?: {
      required?: boolean;
      maxCountOfKeywords?: number;
      maxLength?: number;
      minCountOfKeywords?: number;
      minLength?: number;
    }) => string
  >({
    base: validations =>
      `Tap + to add  ${updatedPlaceHolder}${
        validations?.minCountOfKeywords !== undefined
          ? `. (Minimum ${validations.minCountOfKeywords} required)`
          : ''
      }`,
    md: validations =>
      `Press Enter to add  ${updatedPlaceHolder}${
        validations?.minCountOfKeywords !== undefined
          ? `. (Minimum ${validations.minCountOfKeywords} required)`
          : ''
      }`
  });

  useEffect(() => {
    const value = tempValues || [];

    const nonEmpty = value
      .filter((v: { text: string }) => v?.text?.trim().length > 0)
      .map((v: { text: string }) => v.text.trim());

    setValue(fieldName, nonEmpty, {
      shouldValidate: true,
      shouldDirty: true
    });

    if (nonEmpty.length > 0) {
      form.clearErrors(tempFieldName);
      form.clearErrors(fieldName);
    }
  }, [tempValues, fieldName, setValue]);

  const error = get(errors, fieldName);
  const fieldError = get(errors, tempFieldName);
  const errorMessage = getErrorMessage(fieldError);

  function getErrorMessage(error: any) {
    if (Array.isArray(error)) {
      if (
        'root' in error &&
        error.root &&
        typeof error.root === 'object' &&
        'message' in error.root
      ) {
        return error.root.message;
      }
    }
    return '';
  }

  const handleAddKeyword = () => {
    const input = tagInputWrapperRef.current?.querySelector('input');
    const value = input?.value?.trim();
    if (input && value) {
      const currentValues = form.getValues(tempFieldName) || [];
      form.setValue(tempFieldName, [...currentValues, { text: value }], {
        shouldValidate: true,
        shouldDirty: true
      });
      input.value = '';
    }
  };

  return (
    <Flex flexDirection="column" gap={3} data-cy="keywords-edit-view" mb={4}>
      <Flex alignItems={'flex-start'} gap={2}>
        <FieldLabel
          isRequired={validations?.required}
          label={labels.main}
          subText={!isHidSubtitle ? labels.subtitle : ''}
          data-cy="keywords-field-label"
          helpText={isShowInfo && labels.helpText}
        />
      </Flex>
      <Flex gap={2} alignItems="flex-start" ref={tagInputWrapperRef}>
        <Box
          flex={1}
          sx={{
            span: {
              whiteSpace: 'normal'
            }
          }}
        >
          <Controller
            control={control}
            name={tempFieldName}
            rules={{
              validate: (v: { text: string }[]) => {
                const value = v ?? [];
                const {
                  maxCountOfKeywords,
                  minCountOfKeywords,
                  maxLength,
                  minLength,
                  required
                } = validations ?? {};

                if (required && !value.length)
                  return 'At least 1 item is required';
                if (maxCountOfKeywords && value.length > maxCountOfKeywords)
                  return errorMessageFns.checkMaxCountOfKeywords(
                    maxCountOfKeywords,
                    errorLabel
                  );

                if (minCountOfKeywords && value.length < 1)
                  return errorMessageFns.checkMinCountOfKeywords(
                    minCountOfKeywords,
                    errorLabel
                  );

                const seen = new Set<string>();

                for (const { text } of value) {
                  const trimmed = text?.trim();

                  if (!trimmed) return 'Empty keywords are not allowed';
                  if (maxLength && trimmed.length > maxLength)
                    return errorMessageFns.checkMaxLength(maxLength);
                  if (minLength && trimmed.length < minLength)
                    return errorMessageFns.checkMinLength(minLength);

                  const key = trimmed.toLowerCase();
                  if (seen.has(key))
                    return 'Duplicate keywords are not allowed';
                  seen.add(key);
                }

                return true;
              }
            }}
            render={({ field }) => (
              <TagInput
                control={control}
                {...field}
                isInvalid={!!errorMessage || error?.message}
                errorMessage={error?.message || errorMessage}
                placeholder={inputPlaceholder}
                helperText={helperText?.(validations)}
                onBlur={() => trigger(tempFieldName)}
                data-cy="keywords-tag-input"
                data-field-error={!!error}
                data-error={`${tempFieldName}-error`}
              />
            )}
          />
        </Box>
        <Box display={{ base: 'block', md: 'none' }} mt="6px">
          <Tooltip
            hasArrow
            shouldWrapChildren
            placement="top"
            label="Add"
            borderRadius={4}
          >
            <ButtonIcon
              size="small"
              ariaLabel="add-keyword-button"
              icon={<FontAwesomeIcon icon={faPlus} />}
              data-cy="tag-input-add-button"
              onClick={handleAddKeyword}
            />
          </Tooltip>
        </Box>
      </Flex>
    </Flex>
  );
};

export default InputArrayField;
